import styled from 'styled-components'

import { colors, font, radius, tableType } from './tokens'

/**
 * The grid itself, inside {@link TableShell}. Takes whatever height the shell
 * has left after the pagination bar, so nothing here needs to know how tall
 * that bar is. `min-height: 0` is what lets it shrink below its content — a
 * flex item defaults to `min-height: auto`, which would push the pagination
 * back out of view, which is the bug this replaced.
 */
export const GridViewport = styled.div`
  width: 100%;
  flex: 1;
  min-height: 0;
`

export const TableShell = styled.div`
  width: 100%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  background-color: ${colors.surface.white};
  border: 1px solid ${colors.table.shellBorder};
  border-radius: ${radius.shell}px;
  overflow-x: auto;
  overflow-y: visible;
  position: relative;
  /* Height is measured to the viewport bottom by useFitViewportHeight; this is
     the floor when the chrome above leaves less than that.
     260px was three rows once the design's 54px row height landed, which is
     not a usable grid — below this the page scrolls instead, which is the
     better trade. 46px header + 8 rows + the pagination strip. */
  min-height: 540px;
  font-family: ${font.sans};

  /* ── Design-system table surface (P'Sing, VCN Table.dc.html) ──
     Driven through ag-grid's own theme variables. Overriding alpine's selectors
     instead loses: it paints the same properties from those variables, so a
     direct rule either gets beaten or stacks on top of what alpine already drew
     (which is how the checkbox ended up with two borders). Scoped to this shell
     so no other grid in the app moves. */
  --ag-font-family: ${font.sans};
  --ag-font-size: ${tableType.fontSize}px;
  --ag-background-color: ${colors.surface.white};
  --ag-foreground-color: ${colors.text.default};
  --ag-data-color: ${colors.text.default};

  /* The design's header is white with no fill; alpine's default is #f8f8f8. */
  --ag-header-background-color: ${colors.surface.white};
  --ag-header-foreground-color: ${colors.text.default};
  --ag-header-height: 46px;
  --ag-row-height: 54px;

  /* Horizontal dividers only — the design has no vertical rules. */
  --ag-borders: none;
  --ag-border-color: ${colors.table.divider};
  --ag-row-border-color: ${colors.table.divider};
  --ag-row-border-width: 1px;
  --ag-row-border-style: solid;
  --ag-header-column-separator-display: none;
  --ag-cell-horizontal-border: none;

  --ag-odd-row-background-color: transparent;
  --ag-row-hover-color: ${colors.surface.offWhite};

  .ag-header-cell-text {
    font-weight: ${tableType.headerWeight};
  }

  .ag-cell {
    display: flex;
    align-items: center;
  }

  /* 19x19, 2px border, 4px corner — the design's checkbox.
     ag-grid draws its checkbox as an icon-font glyph (::after renders a
     character from the ag-grid icon font), so the border weight and corner
     radius live inside the font and cannot be reached from CSS. The glyph is
     hidden and the control drawn directly: box on the wrapper, tick and dash
     on ::before. */
  --ag-icon-size: 19px;

  .ag-checkbox-input-wrapper {
    position: relative;
    box-sizing: border-box;
    width: 19px;
    height: 19px;
    border-radius: ${radius.checkbox}px;
    border: 2px solid ${colors.table.checkboxBorder};
    background-color: ${colors.surface.white};
    box-shadow: none;
  }

  .ag-checkbox-input-wrapper::after {
    display: none;
  }

  .ag-checkbox-input-wrapper.ag-checked,
  .ag-checkbox-input-wrapper.ag-indeterminate {
    border-color: ${colors.brand.teal};
    background-color: ${colors.brand.teal};
  }

  .ag-checkbox-input-wrapper::before {
    content: '';
    position: absolute;
    inset: 0;
    background: transparent center / 13px 13px no-repeat;
    background-image: none;
    opacity: 1;
  }

  .ag-checkbox-input-wrapper.ag-checked::before {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23fff' stroke-width='3.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='20 6 9 17 4 12'/%3E%3C/svg%3E");
  }

  .ag-checkbox-input-wrapper.ag-indeterminate::before {
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23fff' stroke-width='3.5' stroke-linecap='round'%3E%3Cline x1='5' y1='12' x2='19' y2='12'/%3E%3C/svg%3E");
  }

  .ant-pagination {
    padding: 16px 24px;
    margin: 0;
  }

  /* Card Mode: a card still pointed at by a benefit whose coverage has ended.
     Row-level, because orphan cleanup scans down a filtered page — the amber
     is caught in peripheral vision while the eye is on another column. The
     inset shadow draws the left accent without the layout shift a border
     would cause on an absolutely positioned ag-grid row. */
  .ag-row.vcn-row-ended {
    background-color: #fffbeb;
    box-shadow: inset 3px 0 0 #faad14;
  }

  /* Re-state hover: the rule above outranks the alpine theme's own hover
     colour, so without this an ended row would look inert under the cursor. */
  .ag-row.vcn-row-ended.ag-row-hover,
  .ag-row.vcn-row-ended.ag-row-selected {
    background-color: #fef3c7;
  }
`

export const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 8px;
  width: 100%;
  flex-shrink: 0;
  border-top: 1px solid #e8e8e8;
`

export const ContentArea = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  min-width: 0;
  flex: 1;
`

export const LoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(255, 255, 255, 0.75);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 5;
  border-radius: 4px;
`

export const Placeholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 320px;
  padding: 40px;
  text-align: center;
  color: #595959;
`
